import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EllipsisVerticalIcon, PlusIcon } from 'lucide-react'
import {
	Avatar, AvatarFallback, AvatarImage,
	Button,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Input,
	Textarea,
} from '@rentflowdev/ui'
import { DEFAULT_LOCALE } from '@/types'
import type { RentalNote } from '@/types'
import { useCreateRentalNoteMutation, useDeleteRentalNoteMutation, useListRentalNotesQuery } from '@/api'

function formatTimestamp(iso: string): string {
	const date = new Date(iso)
	const today = new Date()
	const time = date.toLocaleTimeString(DEFAULT_LOCALE, { hour: '2-digit', minute: '2-digit' })
	if (date.toDateString() === today.toDateString()) return `Сегодня, ${time}`
	return `${date.toLocaleDateString(DEFAULT_LOCALE, { day: 'numeric', month: 'short' })}, ${time}`
}

interface RentalNotesSectionProps {
	rentalId: string
}

export function RentalNotesSection({ rentalId }: RentalNotesSectionProps) {
	const { t } = useTranslation()
	const { data } = useListRentalNotesQuery({ rentalId })
	const [createNote, { isLoading: isCreating }] = useCreateRentalNoteMutation()
	const [deleteNote] = useDeleteRentalNoteMutation()
	const notes: RentalNote[] = data?.notes ?? []

	const [adding,  setAdding]  = useState(false)
	const [title,   setTitle]   = useState('')
	const [content, setContent] = useState('')

	function reset() {
		setAdding(false)
		setTitle('')
		setContent('')
	}

	async function handleSubmit() {
		if (!title.trim()) return
		await createNote({ rentalId, title: title.trim(), content: content.trim() }).unwrap()
		reset()
	}

	return (
		<div className="flex flex-col gap-3">
			{notes.length === 0 && !adding && (
				<p className="text-sm text-(--color-muted-foreground)">{t('rent.notes.empty')}</p>
			)}

			{notes.map((note) => (
				<NoteCard
					key={note.id}
					note={note}
					onDelete={() => void deleteNote({ rentalId, noteId: note.id })}
					t={t}
				/>
			))}

			{adding ? (
				<div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--color-border) p-3">
					<Input
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder={t('rent.notes.titlePlaceholder', { defaultValue: 'Заголовок' })}
						autoFocus
					/>
					<Textarea
						value={content}
						onChange={(e) => setContent(e.target.value)}
						placeholder={t('rent.notes.contentPlaceholder', { defaultValue: 'Текст заметки' })}
						className="min-h-20"
					/>
					<div className="flex justify-end gap-2">
						<Button variant="secondary" size="sm" onClick={reset}>
							{t('rent.notes.cancel', { defaultValue: 'Отмена' })}
						</Button>
						<Button
							size="sm"
							disabled={isCreating || !title.trim()}
							onClick={() => void handleSubmit()}
						>
							{t('rent.notes.submit', { defaultValue: 'Добавить' })}
						</Button>
					</div>
				</div>
			) : (
				<Button
					variant="outline"
					size="sm"
					iconLeft={<PlusIcon className="size-4" />}
					className="self-start"
					onClick={() => setAdding(true)}
				>
					{t('rent.notes.add', { defaultValue: 'Добавить заметку' })}
				</Button>
			)}
		</div>
	)
}

function NoteCard({ note, onDelete, t }: { note: RentalNote; onDelete: () => void; t: (k: string) => string }) {
	return (
		<div className="rounded-(--radius-md) border border-(--color-border) p-4">
			<div className="flex items-start gap-3">
				<h3 className="flex-1 text-sm font-[500] leading-5 text-(--color-foreground)">
					{note.title}
				</h3>
				<DropdownMenu>
					<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('rent.notes.actions.menu')} />}>
						<EllipsisVerticalIcon className="size-4" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="min-w-40">
						<DropdownMenuItem>{t('rent.notes.actions.edit')}</DropdownMenuItem>
						<DropdownMenuItem variant="destructive" onClick={onDelete}>
							{t('rent.notes.actions.delete')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{note.content && (
				<p className="mt-2 text-sm leading-5 text-(--color-foreground)">{note.content}</p>
			)}

			<div className="mt-3 flex items-center justify-between text-xs leading-4 text-(--color-muted-foreground)">
				<div className="flex items-center gap-2">
					{note.authorName && (
						<>
							<Avatar className="size-5 rounded-full">
								{note.authorAvatar && <AvatarImage src={note.authorAvatar} alt={note.authorName} />}
								<AvatarFallback className="text-xs">{note.authorName.charAt(0)}</AvatarFallback>
							</Avatar>
							<span>{note.authorName}</span>
						</>
					)}
				</div>
				<span>{formatTimestamp(note.createdAt)}</span>
			</div>
		</div>
	)
}
